import { proxy, subscribe } from 'valtio'
import { memo, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { isStringAllowed, MessageFormatPart } from '../chatUtils'
import { MessagePart } from './MessageFormatted'
import './Chat.css'
import { isIos, reactKeyForMessage } from './utils'
import Button from './Button'
import { pixelartIcons } from './PixelartIcon'
import { useScrollBehavior } from './hooks/useScrollBehavior'
import { withInjectableUi } from './extendableSystem'
import CollapseTab from './components/replay/CollapseTab'

export type Message = {
  parts: MessageFormatPart[],
  id: number
  timestamp?: number
}

const CHAT_DEFAULT_HEIGHT = 180
const CHAT_TOP_RESERVED_HEIGHT = 96

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const getRootPixelVariable = (name: string, fallback: number) => {
  const value = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name))
  return Number.isFinite(value) ? value : fallback
}

const getChatTimestampLabel = (timestamp?: number) => {
  if (!timestamp) return undefined
  return new Date(timestamp).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

// memo: message objects are identity-stable, so appending a new message (or the
// parent re-rendering on scroll/resize state) no longer re-renders every line.
const MessageLine = memo(({
  message,
  currentPlayerName,
  chatOpened,
  persistent,
}: {
  message: Message
  currentPlayerName?: string
  chatOpened?: boolean
  persistent?: boolean
}) => {
  const [fadeState, setFadeState] = useState<'visible' | 'fading' | 'faded'>('visible')

  useEffect(() => {
    if (persistent) {
      setFadeState('visible')
      return
    }
    if (window.debugStopChatFade) return
    // Start fading after 5 seconds
    const fadeTimeout = setTimeout(() => {
      setFadeState('fading')
    }, 5000)

    // Remove after fade animation (3s) completes
    const removeTimeout = setTimeout(() => {
      setFadeState('faded')
    }, 8000)

    // Cleanup timeouts if component unmounts
    return () => {
      clearTimeout(fadeTimeout)
      clearTimeout(removeTimeout)
    }
  }, [persistent])

  const classes = {
    'chat-message': true,
    'chat-message-fading': !chatOpened && fadeState === 'fading',
    'chat-message-faded': !chatOpened && fadeState === 'faded'
  }

  const timestampLabel = getChatTimestampLabel(message.timestamp)

  return <li className={Object.entries(classes).filter(([, val]) => val).map(([name]) => name).join(' ')} data-time={timestampLabel}>
    {timestampLabel && <span className="chat-message-time">{timestampLabel}</span>}
    <span className="chat-message-text">
      {message.parts.map((msg, i) => {
        // Check if this is a text part that might contain a mention
        if (typeof msg.text === 'string' && currentPlayerName) {
          const parts = msg.text.split(new RegExp(`(@${currentPlayerName})`, 'i'))
          if (parts.length > 1) {
            return parts.map((txtPart, j) => {
              const part = {
                ...msg,
                text: txtPart
              }
              if (txtPart.toLowerCase() === `@${currentPlayerName}`.toLowerCase()) {
                part.color = '#ffa500'
                part.bold = true
                return <MessagePart key={j} part={part} />
              }
              return <MessagePart key={j} part={part} />
            })
          }
        }
        return <MessagePart key={i} part={msg} />
      })}
    </span>
  </li>
})

type Props = {
  messages: Message[]
  usingTouch: boolean
  opacity?: number
  opened?: boolean
  onClose?: () => void
  sendMessage?: (message: string) => Promise<void> | void
  fetchCompletionItems?: (triggerKind: 'implicit' | 'explicit', completeValue: string, fullValue: string, abortController?: AbortController) => Promise<string[] | void>
  // width?: number
  allowSelection?: boolean
  inputDisabled?: string
  placeholder?: string
  chatVanillaRestrictions?: boolean
  debugChatScroll?: boolean
  getPingComplete?: (value: string) => Promise<string[]>
  currentPlayerName?: string
  spellCheckEnabled?: boolean
  onSpellCheckEnabledChange?: (enabled: boolean) => void
  persistentMessages?: boolean
  hideWhenEmpty?: boolean
  collapseEnabled?: boolean
  collapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
}

export const chatInputValueGlobal = proxy({
  value: ''
})

const ChatBase = ({
  messages,
  opacity = 1,
  fetchCompletionItems,
  opened,
  sendMessage,
  onClose,
  usingTouch,
  allowSelection,
  inputDisabled,
  placeholder,
  chatVanillaRestrictions,
  debugChatScroll,
  getPingComplete,
  currentPlayerName,
  spellCheckEnabled = false,
  onSpellCheckEnabledChange,
  persistentMessages = false,
  hideWhenEmpty = true,
  collapseEnabled = false,
  collapsed = false,
  onCollapsedChange
}: Props) => {
  const playerNameValidated = useMemo(() => {
    if (!/^[\w\d_]+$/i.test(currentPlayerName ?? '')) return ''
    return currentPlayerName
  }, [currentPlayerName])

  const sendHistoryRef = useRef(JSON.parse(window.sessionStorage.chatHistory || '[]'))
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [preservedInputValue, setPreservedInputValue] = useState('')
  const [inputKey, setInputKey] = useState(0)
  const pingHistoryRef = useRef(JSON.parse(window.localStorage.pingHistory || '[]'))

  const [completePadText, setCompletePadText] = useState('')
  const completeRequestValue = useRef('')
  const [completionItemsSource, setCompletionItemsSource] = useState([] as string[])
  const [completionItems, setCompletionItems] = useState([] as string[])

  const chatInput = useRef<HTMLInputElement>(null!)
  const chatMessages = useRef<HTMLDivElement>(null)
  const chatResize = useRef<{ pointerId: number, startY: number, startHeight: number } | null>(null)
  const chatHistoryPos = useRef(sendHistoryRef.current.length)
  const commandHistoryPos = useRef(0)
  const inputCurrentlyEnteredValue = useRef('')
  const commandHistoryRef = useRef(sendHistoryRef.current.filter((msg: string) => msg.startsWith('/')))

  const { scrollToBottom, isAtBottom, wasAtBottom, currentlyAtBottom } = useScrollBehavior(chatMessages, { messages, opened })
  const [rightNowAtBottom, setRightNowAtBottom] = useState(false)
  const [chatHeightPx, setChatHeightPx] = useState<number | null>(null)
  const [isChatResizing, setIsChatResizing] = useState(false)

  const getChatMinHeight = () => getRootPixelVariable('--chatHeight', CHAT_DEFAULT_HEIGHT)

  const getChatMaxHeight = () => {
    const rootStyle = getComputedStyle(document.documentElement)
    const uiScale = Number.parseFloat(rootStyle.getPropertyValue('--ui-scale')) || 1
    const chatScale = Number.parseFloat(rootStyle.getPropertyValue('--chatScale')) || 1
    const bottomEdge = chatMessages.current?.getBoundingClientRect().bottom ?? window.innerHeight
    const topReserve = CHAT_TOP_RESERVED_HEIGHT * uiScale
    return Math.max(getChatMinHeight(), (bottomEdge - topReserve) / chatScale)
  }

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const resize = chatResize.current
      if (!resize || event.pointerId !== resize.pointerId) return

      const minHeight = getChatMinHeight()
      const maxHeight = getChatMaxHeight()
      setChatHeightPx(clamp(resize.startHeight + resize.startY - event.clientY, minHeight, maxHeight))
    }

    const handlePointerEnd = (event: PointerEvent) => {
      const resize = chatResize.current
      if (!resize || event.pointerId !== resize.pointerId) return

      chatResize.current = null
      setIsChatResizing(false)
      document.body.classList.remove('chat-resizing')
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerEnd)
    window.addEventListener('pointercancel', handlePointerEnd)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerEnd)
      window.removeEventListener('pointercancel', handlePointerEnd)
      document.body.classList.remove('chat-resizing')
    }
  }, [])

  useEffect(() => {
    if (!opened || chatHeightPx === null) return
    setChatHeightPx(height => height === null ? null : clamp(height, getChatMinHeight(), getChatMaxHeight()))
  }, [opened, chatHeightPx])

  useEffect(() => {
    if (!opened || !currentlyAtBottom) return
    scrollToBottom()
  }, [chatHeightPx, opened])

  useEffect(() => {
    if (!debugChatScroll) return
    const interval = setInterval(() => {
      setRightNowAtBottom(isAtBottom())
    }, 50)
    return () => clearInterval(interval)
  }, [debugChatScroll])

  const setSendHistory = (newHistory: string[]) => {
    sendHistoryRef.current = newHistory
    window.sessionStorage.chatHistory = JSON.stringify(newHistory)
    chatHistoryPos.current = newHistory.length
    // Update command history (only messages starting with /)
    commandHistoryRef.current = newHistory.filter((msg: string) => msg.startsWith('/'))
    commandHistoryPos.current = commandHistoryRef.current.length
  }

  const acceptComplete = (item: string) => {
    const base = completeRequestValue.current === '/' ? '' : getCompleteValue()
    updateInputValue(base + item)
    // Record ping completion in history
    if (item.startsWith('@')) {
      const newHistory = [item, ...pingHistoryRef.current.filter((x: string) => x !== item)].slice(0, 10)
      pingHistoryRef.current = newHistory
      // todo use appStorage
      window.localStorage.pingHistory = JSON.stringify(newHistory)
    }
    chatInput.current.focus()
  }

  const updateInputValue = (newValue: string) => {
    chatInput.current.value = newValue
    onMainInputChange()
    setTimeout(() => {
      chatInput.current.setSelectionRange(newValue.length, newValue.length)
    }, 0)
  }

  const handleArrowUp = () => {
    if (chatHistoryPos.current === 0) return
    if (chatHistoryPos.current === sendHistoryRef.current.length) { // started navigating history
      inputCurrentlyEnteredValue.current = chatInput.current.value
    }
    chatHistoryPos.current--
    updateInputValue(sendHistoryRef.current[chatHistoryPos.current] || '')
  }

  const handleArrowDown = () => {
    if (chatHistoryPos.current === sendHistoryRef.current.length) return
    chatHistoryPos.current++
    updateInputValue(sendHistoryRef.current[chatHistoryPos.current] || inputCurrentlyEnteredValue.current || '')
  }

  const handleCommandArrowUp = () => {
    if (commandHistoryPos.current === 0 || commandHistoryRef.current.length === 0) return
    if (commandHistoryPos.current === commandHistoryRef.current.length) { // started navigating command history
      inputCurrentlyEnteredValue.current = chatInput.current.value
    }
    commandHistoryPos.current--
    updateInputValue(commandHistoryRef.current[commandHistoryPos.current] || '')
  }

  const handleCommandArrowDown = () => {
    if (commandHistoryPos.current === commandHistoryRef.current.length) return
    commandHistoryPos.current++
    updateInputValue(commandHistoryRef.current[commandHistoryPos.current] || inputCurrentlyEnteredValue.current || '')
  }

  const auxInputFocus = (direction: 'up' | 'down') => {
    chatInput.current.focus()
    if (direction === 'up') {
      handleArrowUp()
    } else {
      handleArrowDown()
    }
  }

  useEffect(() => {
    // todo focus input on any keypress except tab
  }, [])

  const resetCompletionItems = () => {
    setCompletionItemsSource([])
    setCompletionItems([])
  }

  useEffect(() => {
    if (opened) {
      updateInputValue(chatInputValueGlobal.value)
      chatInputValueGlobal.value = ''
      chatHistoryPos.current = sendHistoryRef.current.length
      commandHistoryPos.current = commandHistoryRef.current.length
      if (!usingTouch) {
        chatInput.current.focus()
      }

      // Add keyboard event listener for letter keys and paste
      const handleKeyDown = (e: KeyboardEvent) => {
        if (['input', 'textarea', 'select'].includes(document.activeElement?.tagName.toLowerCase() ?? '')) return
        // Check if it's a single character key (works with any layout) without modifiers except shift
        const isSingleChar = e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey
        // Check if it's paste command
        const isPaste = e.code === 'KeyV' && (e.ctrlKey || e.metaKey)

        if ((isSingleChar || isPaste) && document.activeElement !== chatInput.current) {
          chatInput.current.focus()
        }
      }

      window.addEventListener('keydown', handleKeyDown)
      const unsubscribeValtio = subscribe(chatInputValueGlobal, () => {
        if (!chatInputValueGlobal.value) return
        updateInputValue(chatInputValueGlobal.value)
        chatInputValueGlobal.value = ''
        chatInput.current.focus()
      })

      return () => {
        window.removeEventListener('keydown', handleKeyDown)
        unsubscribeValtio()
      }
    }
  }, [opened])

  useMemo(() => {
    if (opened) {
      completeRequestValue.current = ''
      resetCompletionItems()
    } else {
      setPreservedInputValue('')
    }
  }, [opened])

  const onMainInputChange = () => {
    const lastWord = chatInput.current.value.slice(0, chatInput.current.selectionEnd ?? chatInput.current.value.length).split(' ').at(-1)!
    const isCommand = chatInput.current.value.startsWith('/')

    if (lastWord.startsWith('@') && getPingComplete && !isCommand) {
      setCompletePadText(lastWord)
      void fetchPingCompletions(true, lastWord.slice(1))
      return
    }

    const completeValue = getCompleteValue()
    setCompletePadText(completeValue === '/' ? '' : completeValue)
    // not sure if enabling would be useful at all (maybe make as a setting in the future?)
    // setSpellCheckEnabled(!chatInput.current.value.startsWith('/'))
    if (completeRequestValue.current === completeValue) {
      updateFilteredCompleteItems(completionItemsSource)
      return
    }

    if (completeValue.startsWith('/')) {
      void fetchCompletions(true)
    } else {
      resetCompletionItems()
    }
    completeRequestValue.current = completeValue
  }

  const fetchCompletions = async (implicit: boolean, inputValue = chatInput.current.value) => {
    const completeValue = getCompleteValue(inputValue)
    completeRequestValue.current = completeValue
    resetCompletionItems()
    const newItems = await fetchCompletionItems?.(implicit ? 'implicit' : 'explicit', completeValue, inputValue) ?? []
    if (completeValue !== completeRequestValue.current) return
    setCompletionItemsSource(newItems)
    updateFilteredCompleteItems(newItems)
  }

  const fetchPingCompletions = async (implicit: boolean, inputValue: string) => {
    completeRequestValue.current = inputValue
    resetCompletionItems()
    const newItems = await getPingComplete?.(inputValue) ?? []
    if (inputValue !== completeRequestValue.current) return
    // Sort items by ping history
    const sortedItems = [...newItems].sort((a, b) => {
      const aIndex = pingHistoryRef.current.indexOf(a)
      const bIndex = pingHistoryRef.current.indexOf(b)
      if (aIndex === -1 && bIndex === -1) return 0
      if (aIndex === -1) return 1
      if (bIndex === -1) return -1
      return aIndex - bIndex
    })
    setCompletionItemsSource(sortedItems)
    updateFilteredCompleteItems(sortedItems)
  }

  const updateFilteredCompleteItems = (sourceItems: string[] | Array<{ match: string, toolip: string }>) => {
    const newCompleteItems = sourceItems
      .map((item): string => (typeof item === 'string' ? item : item.match))
      .filter(item => {
        // this regex is imporatnt is it controls the word matching
        // const compareableParts = item.split(/[[\]{},_:]/)
        const lastWord = chatInput.current.value.slice(0, chatInput.current.selectionEnd ?? chatInput.current.value.length).split(' ').at(-1)!
        if (lastWord.startsWith('@')) {
          return item.toLowerCase().includes(lastWord.slice(1).toLowerCase())
        }
        return item.includes(lastWord)
        // return [item, ...compareableParts].some(compareablePart => compareablePart.startsWith(lastWord))
      })
    setCompletionItems(newCompleteItems)
  }

  const getDefaultCompleteValue = () => {
    const raw = chatInput.current.value
    return raw.slice(0, chatInput.current.selectionEnd ?? raw.length)
  }

  const getCompleteValue = (value = getDefaultCompleteValue()) => {
    const valueParts = value.split(' ')
    const lastLength = valueParts.at(-1)!.length
    const completeValue = lastLength ? value.slice(0, -lastLength) : value
    if (valueParts.length === 1 && value.startsWith('/')) return '/'
    return completeValue
  }

  const handleSlashCommand = () => {
    remountInput('/')
  }

  const handleAcceptFirstCompletion = () => {
    if (completionItems.length > 0) {
      acceptComplete(completionItems[0])
    }
  }

  const remountInput = (newValue?: string) => {
    if (newValue !== undefined) {
      setPreservedInputValue(newValue)
    }
    setInputKey(k => k + 1)
  }

  useEffect(() => {
    if (preservedInputValue && chatInput.current) {
      chatInput.current.focus()
    }
  }, [inputKey]) // Changed from spellCheckEnabled to inputKey

  const hasRenderableMessages = messages.some(message => {
    return message.parts.some(part => typeof part.text === 'string' && part.text.trim().length > 0)
  })
  const shouldRenderMessages = !hideWhenEmpty || hasRenderableMessages || debugChatScroll
  const chatWrapperStyle = {
    userSelect: opened && allowSelection ? 'text' : undefined,
    '--chat-user-height': chatHeightPx === null ? undefined : `${chatHeightPx}px`,
  } as CSSProperties

  return (
    <>
      {shouldRenderMessages && (
        <div
          className={[
            'chat-wrapper',
            'chat-messages-wrapper',
            usingTouch ? 'display-mobile' : '',
            opened ? 'chat-opened' : '',
            collapseEnabled ? 'chat-collapse-enabled' : '',
            collapsed ? 'chat-collapsed' : '',
            isChatResizing ? 'chat-resizing-active' : '',
          ].filter(Boolean).join(' ')}
          style={chatWrapperStyle}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          data-replay-ui={collapseEnabled || undefined}
        >
          {opened && !usingTouch && !collapsed && (
            <div
              className="chat-resize-handle"
              title="Изменить высоту чата"
              onPointerDown={(event) => {
                event.preventDefault()
                event.stopPropagation()

                const minHeight = getChatMinHeight()
                const maxHeight = getChatMaxHeight()
                const currentHeight = chatHeightPx ?? getRootPixelVariable('--chatHeight', CHAT_DEFAULT_HEIGHT)

                chatResize.current = {
                  pointerId: event.pointerId,
                  startY: event.clientY,
                  startHeight: clamp(currentHeight, minHeight, maxHeight),
                }
                event.currentTarget.setPointerCapture(event.pointerId)
                setIsChatResizing(true)
                document.body.classList.add('chat-resizing')
              }}
            />
          )}
          {opacity && <div ref={chatMessages} className={`chat ${opened ? 'opened' : ''}`} id="chat-messages" style={{ opacity }}>
            {debugChatScroll && (
              <div
                style={{
                  position: 'absolute',
                  top: 5,
                  left: 5,
                  display: 'flex',
                  gap: 4,
                  zIndex: 100,
                }}
              >
                <div
                  title="Right now is at bottom (updated every 50ms)"
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: rightNowAtBottom ? '#00ff00' : '#ff0000',
                    border: '1px solid #fff',
                  }}
                />
                <div
                  title="Currently at bottom"
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: currentlyAtBottom ? '#00ff00' : '#ff0000',
                    border: '1px solid #fff',
                  }}
                />
                <div
                  title="Was at bottom"
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: wasAtBottom() ? '#00ff00' : '#ff0000',
                    border: '1px solid #fff',
                  }}
                />
                <div
                  title="Chat opened"
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: opened ? '#00ff00' : '#ff0000',
                    border: '1px solid #fff',
                  }}
                />
              </div>
            )}
            {messages.map((m) => (
              <MessageLine
                key={reactKeyForMessage(m)}
                message={m}
                currentPlayerName={playerNameValidated}
                chatOpened={opened}
                persistent={persistentMessages}
              />
            ))}
          </div> || undefined}
          {collapseEnabled && (
            <CollapseTab
              edge="right"
              collapsed={collapsed}
              onToggle={() => onCollapsedChange?.(!collapsed)}
              title={collapsed ? 'Развернуть чат' : 'Свернуть чат'}
            />
          )}
        </div>
      )}

      <div className={`chat-wrapper chat-input-wrapper ${usingTouch ? 'input-mobile' : ''}`} hidden={!opened || collapsed}>
        {usingTouch && (
          <>
            <Button
              icon={pixelartIcons.close}
              onClick={() => onClose?.()}
              style={{
                width: 20,
                flexShrink: 0,
              }}
            />

            {(chatInput.current?.value && !chatInput.current.value.startsWith('/')) ? (
              // TOGGLE SPELL CHECK
              <Button
                style={{
                  width: 20,
                  flexShrink: 0,
                }}
                overlayColor={spellCheckEnabled ? '#00ff00' : '#ff0000'}
                icon={pixelartIcons['text-wrap']}
                onClick={() => {
                  setPreservedInputValue(chatInput.current?.value || '')
                  onSpellCheckEnabledChange?.(!spellCheckEnabled)
                  remountInput()
                }}
              />
            ) : (
              // SLASH COMMAND
              <Button
                style={{
                  width: 20,
                  flexShrink: 0,
                }}
                label={chatInput.current?.value ? undefined : '/'}
                icon={chatInput.current?.value ? pixelartIcons['arrow-right'] : undefined}
                onClick={() => {
                  const inputValue = chatInput.current.value
                  if (!inputValue) {
                    handleSlashCommand()
                  } else if (completionItems.length > 0) {
                    handleAcceptFirstCompletion()
                  }
                }}
              />
            )}
          </>
        )}
        <div className="chat-input">
          {isInputFocused && completionItems?.length ? (
            <div className="chat-completions">
              <div className="chat-completions-pad-text">{completePadText}</div>
              <div className="chat-completions-items">
                {completionItems.map((item) => (
                  <div
                    key={item}
                    onMouseDown={(e) => {
                      e.preventDefault() // Prevent blur before click
                      acceptComplete(item)
                    }}
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <form onSubmit={async (e) => {
            e.preventDefault()
            const message = chatInput.current.value
            if (message) {
              setSendHistory([...sendHistoryRef.current, message])
              onClose?.()
              await sendMessage?.(message)
              // Always scroll to bottom after sending a message
              scrollToBottom()
            }
          }}
          >
            {isIos && <input
              value=''
              type="text"
              className="chat-mobile-input-hidden chat-mobile-input-hidden-up"
              id="chatinput-next-command"
              spellCheck={false}
              autoComplete="off"
              onFocus={() => auxInputFocus('up')}
              onChange={() => { }}
            />}
            <input
              maxLength={chatVanillaRestrictions ? 256 : undefined}
              defaultValue={preservedInputValue}
              // ios doesn't support toggling autoCorrect on the fly so we need to re-create the input
              key={`${inputKey}`}
              autoCapitalize={preservedInputValue ? 'off' : 'on'}
              ref={chatInput}
              type="text"
              className="chat-input"
              id="chatinput"
              spellCheck={spellCheckEnabled}
              autoCorrect={spellCheckEnabled ? 'on' : 'off'}
              autoComplete="off"
              aria-autocomplete="both"
              onChange={onMainInputChange}
              disabled={!!inputDisabled}
              placeholder={inputDisabled || placeholder}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              onKeyDown={(e) => {
                if (e.code === 'ArrowUp') {
                  if (e.altKey) {
                    handleCommandArrowUp()
                    e.preventDefault()
                  } else {
                    handleArrowUp()
                  }
                } else if (e.code === 'ArrowDown') {
                  if (e.altKey) {
                    handleCommandArrowDown()
                    e.preventDefault()
                  } else {
                    handleArrowDown()
                  }
                }
                if (e.code === 'Tab') {
                  if (completionItemsSource.length) {
                    if (completionItems.length) {
                      acceptComplete(completionItems[0])
                    }
                  } else {
                    void fetchCompletions(false)
                  }
                  e.preventDefault()
                }
                if (e.code === 'Space') {
                  resetCompletionItems()
                  if (chatInput.current.value.startsWith('/')) {
                    // alternative we could just simply use keyup, but only with keydown we can display suggestions popup as soon as possible
                    void fetchCompletions(true, getCompleteValue(getDefaultCompleteValue() + ' '))
                  }
                }
              }}
            />
            {isIos && <input
              value=''
              type="text"
              className="chat-mobile-input-hidden chat-mobile-input-hidden-down"
              id="chatinput-prev-command"
              spellCheck={false}
              autoComplete="off"
              onFocus={() => auxInputFocus('down')}
              onChange={() => { }}
            />}
            {/* for some reason this is needed to make Enter work on android chrome */}
            <button type='submit' className="chat-submit-button" />
          </form>
        </div>
      </div>
    </>
  )
}

export default withInjectableUi(ChatBase, 'chat')
